import apiService from '../Api'
import { Teacher, TeacherComplete, KnowledgeArea, Course, TeachingFormat } from './types'

export interface ITeacherFields {
  name: string
  banner_picture: File | null
  profile_picture: File | null
  profile_picture_thumbnail: File | null
  iam_picture: File | null
  highlight_phrase: string
  iam_text: string
  staff_body_text: string
  teaching_formats: TeachingFormat[]
  knowledge_areas: number[]
  courses: number[]
  enabled: boolean
}

export class TeacherService {
  async list(queryParams: {
    search: string
    length: string
    page: number
  }): Promise<{ data: Teacher[]; total_filtered: number; total: number }> {
    const params = new URLSearchParams()
    params.append('search', queryParams.search)
    params.append('length', queryParams.length)
    params.append('start', ((queryParams.page - 1) * parseInt(queryParams.length)).toString())

    return apiService.get('/teachers', { params }).then((response) => response.data)
  }

  async get(id: number): Promise<TeacherComplete> {
    return apiService.get(`/teachers/${id}`).then((response) => response.data)
  }

  async delete(id: number): Promise<void> {
    return apiService.delete(`/teachers/${id}`)
  }

  async getKnowledgeAreas(): Promise<KnowledgeArea[]> {
    return apiService.get('/knowledge-areas').then((response) => response.data.areas)
  }

  async getCourses(): Promise<Course[]> {
    return apiService.get('/courses').then((response) => response.data.data)
  }

  async create(form: ITeacherFields): Promise<TeacherComplete> {
    const formData = new FormData()
    formData.append('name', form.name)

    if (form.banner_picture) {
      formData.append('banner_picture', form.banner_picture)
    }

    if (form.profile_picture) {
      formData.append('profile_picture', form.profile_picture)
    }

    if (form.profile_picture_thumbnail) {
      formData.append('profile_picture_thumbnail', form.profile_picture_thumbnail)
    }

    if (form.iam_picture) {
      formData.append('iam_picture', form.iam_picture)
    }

    formData.append('highlight_phrase', form.highlight_phrase)
    formData.append('iam_text', form.iam_text)
    formData.append('staff_body_text', form.staff_body_text)
    formData.append('enabled', form.enabled ? '1' : '0')

    form.teaching_formats.forEach((format) => {
      formData.append('teaching_formats[]', format)
    })

    form.knowledge_areas.forEach((areaId) => {
      formData.append('knowledge_areas[]', areaId.toString())
    })

    form.courses.forEach((courseId) => {
      formData.append('courses[]', courseId.toString())
    })

    return apiService.post('/teachers', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  }

  async update(id: number, form: ITeacherFields): Promise<TeacherComplete> {
    const formData = new FormData()
    formData.append('_method', 'PUT')
    formData.append('name', form.name)

    if (form.banner_picture) {
      formData.append('banner_picture', form.banner_picture)
    }

    if (form.profile_picture) {
      formData.append('profile_picture', form.profile_picture)
    }

    if (form.profile_picture_thumbnail) {
      formData.append('profile_picture_thumbnail', form.profile_picture_thumbnail)
    }

    if (form.iam_picture) {
      formData.append('iam_picture', form.iam_picture)
    }

    formData.append('highlight_phrase', form.highlight_phrase)
    formData.append('iam_text', form.iam_text)
    formData.append('staff_body_text', form.staff_body_text)
    formData.append('enabled', form.enabled ? '1' : '0')

    form.teaching_formats.forEach((format) => {
      formData.append('teaching_formats[]', format)
    })

    form.knowledge_areas.forEach((areaId) => {
      formData.append('knowledge_areas[]', areaId.toString())
    })

    form.courses.forEach((courseId) => {
      formData.append('courses[]', courseId.toString())
    })

    return apiService.post(`/teachers/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  }
}
